import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridController } from './compositor/GridController';
import { SharedState } from './worker/SharedState';
import FuseWorker from './worker/worker?worker';
import { ViewportStreamer } from './data/ViewportStreamer';
import { WorkerBus } from './data/WorkerBus';
import { AppWorkerClient } from './data/AppWorkerClient';
import { TabManager } from './data/TabManager';
import type { TabSummary } from './data/TabManager';
import { ServerApiClient } from './data/ServerApiClient';
import {
  readDevImpersonationSession,
  restoreDevImpersonationSession,
  subscribeDevImpersonationSession,
  type DevImpersonationSession,
} from './data/devImpersonationSession';
import { ensureApiSessionReady, useApiSessionSnapshot } from './data/apiSession';
import { managedOAuthAccountLinkMode } from './data/managedOAuthAccountLink';
import { WorkbookSessionFacade } from './data/WorkbookSessionFacade';
import {
  type SpreadsheetEngineOperationEnvelope,
} from './protocol/spreadsheetEngineProtocol';
import { isFuseGridNativeDesktopShell } from './data/desktop/isDesktop';
import { CfoCoreApp } from './components/parity-dev/cfo/CfoCoreApp'
import { LoadingOverlay } from './components/LoadingOverlay'
import { ClientOperatingWorkspacePage } from './components/client-workspace/ClientOperatingWorkspacePage'
import { CfoJobInputsPage } from './components/parity-dev/cfo/CfoJobInputs'
import { cfoRouteNeedsAppWorker } from './components/parity-dev/cfo/cfoRoutes'
import { TableauAdminShell } from './components/parity-dev/TableauAdminShell'
import { ExcelChromeMockups } from './components/excel/ExcelChromeMockups'
import { ExcelLiveChromeFixture } from './components/excel/ExcelLiveChromeFixture'
import {
  FuseGridSurfaceAdminFixture,
  FuseGridSurfaceDataSourceFixture,
  FuseGridSurfaceVizAuthoringFixture,
  FuseGridSurfaceVizEditFixture,
} from './components/chrome/FuseGridSurfaceParityFixtures'
import { readAppRouteMode, type AppRouteMode } from './appRouteMode'
import { isHybridReportLaunchLocation } from './hybridReportLaunch'
import { CfoPublicReportPage } from './components/parity-dev/cfo/CfoPublicReportPage'
import { FounderConsole } from './components/founder/FounderConsole'
import { CfoLandingPage } from './components/cfo-site/CfoLandingPage'
import { CfoPricingPage } from './components/cfo-site/CfoPricingPage'
import { CfoSignupPage } from './components/cfo-site/CfoSignupPage'
import { CfoDemoEntryPage } from './components/cfo-site/CfoDemoEntryPage'
import { CfoDemoBanner } from './components/cfo-site/CfoDemoBanner'
import { TourHost, TourShareEntryPage } from './components/tour'
import { CfoExperiencesPage } from './components/cfo-site/CfoExperiencesPage'
import { demoHomeRedirectPath } from './data/demoSession'
import { CfoFirmCockpitApp } from './components/parity-dev/cfo/CfoFirmCockpitApp'
import { CfoSyncStartedPage } from './components/cfo-site/CfoSyncStartedPage'
import { CfoConnectChecklistPage } from './components/cfo-site/CfoConnectChecklistPage'
import { CfoLegalPage } from './components/cfo-site/CfoLegalPage'
import { CfoCheckoutCompletePage } from './components/cfo-site/CfoCheckoutCompletePage'
import { CfoPartnerBillingPage } from './components/cfo-site/CfoPartnerBillingPage'
import {
  initAnalytics,
  installGlobalActivityTelemetry,
  useRouteActivityTelemetry,
} from './analytics'
import './App.css';
import { installDiagnostics } from './perf/diagnostics';
import { isFieldMappingConsoleEnabled, isFieldMappingDebugEnabled, pushFieldMappingTrace } from './utils/fieldMappingDebug';
import { requestPersistentStorage } from './utils/storageHeadroom';
import {
  getActiveWorksheetCompilerBackendId,
  setWorksheetCompilerBackend,
  setWorksheetCompilerStrictSwitchMode,
} from './tableau/worksheetCompilerAdapter'
import {
  createRustWorksheetCompilerBackend,
  initRustWorksheetCompilerBackendAsync,
} from './tableau/worksheetCompilerRustBackend'

const TableauCreatorPage = lazy(() =>
  import('./components/parity-dev/TableauCreatorPage').then((module) => ({
    default: module.TableauCreatorPage,
  })),
)

const USE_WORKER = true;
let lastUiZoomApplySignature = '';
type CollabStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

const DEV_URL_BAR_HEIGHT_PX = 36
const DEV_IMPERSONATION_BAR_HEIGHT_PX = 34
const compilerFlagIsTruthy = (value: any): boolean =>
  ['1', 'true', 'yes', 'on'].includes(String(value ?? '').trim().toLowerCase())

const shouldUseTsLegacyCompilerKillSwitch = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const sp = new URLSearchParams(window.location.search)
    const queryValue = sp.get('fg_use_ts_legacy_compiler') ?? ''
    if (queryValue) return compilerFlagIsTruthy(queryValue)
  } catch { }
  try {
    return compilerFlagIsTruthy(window.localStorage.getItem('fg_use_ts_legacy_compiler'))
  } catch {
    return false
  }
}

let mainThreadWorksheetCompilerBootstrapPromise: Promise<void> | null = null

const ensureMainThreadWorksheetCompilerBootstrapped = async (): Promise<void> => {
  if (typeof window === 'undefined') return
  if (shouldUseTsLegacyCompilerKillSwitch()) {
    setWorksheetCompilerStrictSwitchMode('typed-required')
    return
  }
  if (getActiveWorksheetCompilerBackendId() === 'rust') return
  if (!mainThreadWorksheetCompilerBootstrapPromise) {
    mainThreadWorksheetCompilerBootstrapPromise = (async () => {
      try {
        await initRustWorksheetCompilerBackendAsync()
        setWorksheetCompilerBackend(createRustWorksheetCompilerBackend())
        setWorksheetCompilerStrictSwitchMode('typed-required')
        console.log('[App] Rust worksheet compiler backend installed (main thread)')
      } catch (error) {
        setWorksheetCompilerStrictSwitchMode('typed-required')
        console.error(
          '[App] Failed to install Rust worksheet compiler backend on main thread; worksheet planning will fail closed instead of using TS legacy.',
          error,
        )
      }
    })()
  }
  await mainThreadWorksheetCompilerBootstrapPromise
}

console.log('[App] Environment Check:', {
  crossOriginIsolated: window.crossOriginIsolated,
  sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
  secure: window.isSecureContext
});

type FgConsoleEntry = {
  ts: number
  level: 'log' | 'info' | 'warn' | 'error' | 'debug'
  text: string
}

function formatConsoleArgs(args: any[]): string {
  try {
    return args
      .map((a) => {
        if (typeof a === 'string') return a
        if (a instanceof Error) return a.stack || a.message || String(a)
        try { return JSON.stringify(a) } catch { return String(a) }
      })
      .join(' ')
  } catch {
    return String(args)
  }
}

function isTableauCreatorRoute(
  _loc: Pick<Location, 'pathname'> | null | undefined,
): boolean {
  return true
}

// Public, unauthenticated board-pack share link: /share/cfo/:token
function publicCfoShareToken(loc: Location | null | undefined): string | null {
  const parts = String(loc?.pathname || '').split('/').filter(Boolean)
  if (parts.length >= 3 && parts[0] === 'share' && parts[1] === 'cfo') {
    return decodeURIComponent(parts[2])
  }
  return null
}

function devLoginBridgeMode(loc: Location | null | undefined): 'issue' | 'redeem' | null {
  if (!loc) return null
  const pathname = String(loc.pathname || '')
  if (pathname === '/auth/dev-login/issue') return 'issue'
  if (pathname === '/auth/dev-login/redeem') return 'redeem'
  try {
    const sp = new URLSearchParams(loc.search || '')
    const mode = String(sp.get('dev_login_bridge') || '').trim().toLowerCase()
    if (mode === 'issue' || mode === 'redeem') return mode
  } catch { }
  return null
}

type OAuthConsentContract = {
  id: string
  display_name: string
  workbooks: string[]
}

type OAuthConsentDetails = {
  request_id: string
  client_name: string
  scope: string
  user_email: string
  contracts: OAuthConsentContract[]
}

function oauthAuthorizationRequest(loc: Location | null | undefined): string | null {
  if (!loc || String(loc.pathname || '') !== '/oauth/authorize') return null
  const requestId = String(new URLSearchParams(loc.search || '').get('request') || '').trim()
  return /^[0-9a-f-]{36}$/i.test(requestId) ? requestId : null
}

function isManagedAccountLinkRoute(loc: Location | null | undefined): boolean {
  return String(loc?.pathname || '') === '/oauth/managed-account-link'
}

type ManagedLinkConfig = {
  authorization_endpoint: string
  token_endpoint: string
  client_id: string
  redirect_uri: string
  resource: string
  scopes: string[]
  contracts: Array<{ id: string; display_name: string }>
}

type ManagedAccountLink = {
  id: string | null
  status: 'active' | 'revoked' | string | null
  linked_at: string | null
  revoked_at: string | null
  contract: string | null
}

type DirectOAuthConnection = {
  client_id: string
  client_name: string
  contract_id: string
  contract: string
  workbooks: string[]
  connected_at: string
  last_used_at: string | null
  refresh_expires_at: string | null
}

const base64Url = (bytes: Uint8Array): string => {
  let binary = ''
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return window.btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/g, '')
}

function ManagedOAuthAccountLinkPage() {
  const [config, setConfig] = useState<ManagedLinkConfig | null>(null)
  const [links, setLinks] = useState<ManagedAccountLink[]>([])
  const [directConnections, setDirectConnections] = useState<DirectOAuthConnection[]>([])
  const [contractId, setContractId] = useState('')
  const [status, setStatus] = useState('Loading managed OAuth configuration…')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const externalAuthId = useMemo(() => String(new URLSearchParams(window.location.search).get('external_auth_id') || ''), [])

  const loadConnectorState = useCallback(async (token: string) => {
    const api = new ServerApiClient()
    const [configResponse, linksResponse, directResponse] = await Promise.all([
      fetch(`${api.baseUrl}/api/platform/managed-oauth/account-link/config`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }),
      fetch(`${api.baseUrl}/api/platform/managed-oauth/account-link`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }),
      fetch(`${api.baseUrl}/api/platform/oauth/connections`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }),
    ])
    const configBody = await configResponse.json().catch(() => ({}))
    const linksBody = await linksResponse.json().catch(() => ({}))
    const directBody = await directResponse.json().catch(() => ({}))
    if (!configResponse.ok) throw new Error(String(configBody.error || 'Managed OAuth is not available.'))
    if (!linksResponse.ok) throw new Error(String(linksBody.error || 'FuseGrid could not load connector status.'))
    if (!directResponse.ok) throw new Error(String(directBody.error || 'FuseGrid could not load direct connector status.'))
    const next = configBody as ManagedLinkConfig
    setConfig(next)
    const nextLinks = Array.isArray(linksBody.links) ? linksBody.links as ManagedAccountLink[] : []
    const nextDirectConnections = Array.isArray(directBody.connections) ? directBody.connections as DirectOAuthConnection[] : []
    setLinks(nextLinks)
    setDirectConnections(nextDirectConnections)
    if (next.contracts.length === 1) setContractId(next.contracts[0].id)
    return { config: next, links: nextLinks, directConnections: nextDirectConnections }
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const session = await ensureApiSessionReady()
        if (!session.token) {
          window.location.href = `/login?next=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`
          return
        }
        const api = new ServerApiClient()
        const params = new URLSearchParams(window.location.search)
        const code = params.get('code')
        const returnedState = params.get('state')
        if (code) {
          setStatus('Verifying the managed identity and linking it to FuseGrid…')
          const stored = JSON.parse(window.sessionStorage.getItem('fg_managed_oauth_link') || '{}')
          if (!returnedState || returnedState !== stored.state || !stored.verifier || !stored.config || !stored.contractId) throw new Error('The account-link state is invalid or expired.')
          const storedConfig = stored.config as ManagedLinkConfig
          const tokenResponse = await fetch(storedConfig.token_endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ grant_type: 'authorization_code', client_id: storedConfig.client_id, code, redirect_uri: storedConfig.redirect_uri, code_verifier: stored.verifier }),
          })
          const tokens = await tokenResponse.json().catch(() => ({}))
          if (!tokenResponse.ok || !tokens.access_token) throw new Error(String(tokens.error_description || tokens.error || 'The managed provider did not return an access token.'))
          const linked = await fetch(`${api.baseUrl}/api/platform/managed-oauth/account-link`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ access_token: tokens.access_token, contract_id: stored.contractId }),
          })
          const body = await linked.json().catch(() => ({}))
          if (!linked.ok) throw new Error(String(body.error || 'FuseGrid could not activate the account link.'))
          window.sessionStorage.removeItem('fg_managed_oauth_link')
          window.history.replaceState({}, '', window.location.pathname)
          await loadConnectorState(session.token)
          setStatus('FuseGrid is connected. The AI connector rechecks your current FuseGrid access on every call.')
          return
        }
        const next = await loadConnectorState(session.token)
        if (cancelled) return
        const active = next.links.some((link) => link.status === 'active') || next.directConnections.length > 0
        const mode = managedOAuthAccountLinkMode({
          hasActiveLink: active,
          externalAuthId,
          publicClientId: next.config.client_id,
        })
        setStatus(mode === 'provider_completion'
          ? 'Confirm which FuseGrid workspace the connected application may access.'
          : mode === 'connected'
            ? 'Review or disconnect your current AI connector.'
            : mode === 'direct_link'
              ? 'Choose the FuseGrid workspace this AI connector may access.'
              : 'FuseGrid is disconnected. Start a new connection from your AI provider; it will return you here to choose a workspace.')
      } catch (reason: any) {
        if (!cancelled) setError(reason?.message || String(reason))
      }
    }
    void run()
    return () => { cancelled = true }
  }, [externalAuthId, loadConnectorState])

  const disconnect = async () => {
    if (!window.confirm('Disconnect this AI connector from FuseGrid? Its FuseGrid data access will stop immediately.')) return
    setBusy(true)
    setError(null)
    try {
      const session = await ensureApiSessionReady()
      if (!session.token) throw new Error('Your FuseGrid session has expired.')
      const api = new ServerApiClient()
      const response = await fetch(`${api.baseUrl}/api/platform/managed-oauth/account-link`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.token}` },
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(String(body.error || 'FuseGrid could not disconnect the connector.'))
      await loadConnectorState(session.token)
      setStatus(config?.client_id
        ? 'FuseGrid access is disconnected. Choose a workspace below to reconnect.'
        : 'FuseGrid access is disconnected. Start a new connection from your AI provider; it will return you here to choose a workspace.')
    } catch (reason: any) {
      setError(reason?.message || String(reason))
    } finally {
      setBusy(false)
    }
  }

  const disconnectDirect = async (connection: DirectOAuthConnection) => {
    if (!window.confirm(`Disconnect ${connection.client_name} from the ${connection.contract} workspace? FuseGrid data access will stop immediately.`)) return
    setBusy(true)
    setError(null)
    try {
      const session = await ensureApiSessionReady()
      if (!session.token) throw new Error('Your FuseGrid session has expired.')
      const api = new ServerApiClient()
      const response = await fetch(`${api.baseUrl}/api/platform/oauth/connections`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: connection.client_id, contract_id: connection.contract_id }),
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(String(body.error || 'FuseGrid could not disconnect the connector.'))
      await loadConnectorState(session.token)
      setStatus(`${connection.client_name} is disconnected. Start a new connection from that AI provider whenever you want to reconnect.`)
    } catch (reason: any) {
      setError(reason?.message || String(reason))
    } finally {
      setBusy(false)
    }
  }

  const connect = async () => {
    if (!config || !contractId) return
    setBusy(true)
    setError(null)
    try {
      if (externalAuthId) {
        const session = await ensureApiSessionReady()
        if (!session.token) throw new Error('Your FuseGrid session has expired.')
        const api = new ServerApiClient()
        const response = await fetch(`${api.baseUrl}/api/platform/managed-oauth/account-link/complete`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ external_auth_id: externalAuthId, contract_id: contractId }),
        })
        const body = await response.json().catch(() => ({}))
        if (!response.ok || !body.redirect_uri) {
          if (body.error === 'oauth_link_expired' || body.restart_required === true) {
            throw new Error('This authorization link has already been used or expired. Return to the connected application and start a new FuseGrid connection.')
          }
          if (body.error === 'oauth_provider_unavailable') {
            throw new Error('WorkOS is temporarily unavailable. Start a new FuseGrid connection from the connected application after the provider recovers.')
          }
          throw new Error(String(body.error || 'The managed OAuth authorization could not be completed.'))
        }
        window.location.assign(String(body.redirect_uri))
        return
      }
      if (!config.client_id) throw new Error('The managed OAuth client is not configured for direct account linking.')
      const verifierBytes = crypto.getRandomValues(new Uint8Array(48))
      const verifier = base64Url(verifierBytes)
      const challenge = base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))))
      const state = base64Url(crypto.getRandomValues(new Uint8Array(24)))
      window.sessionStorage.setItem('fg_managed_oauth_link', JSON.stringify({ state, verifier, contractId, config }))
      const url = new URL(config.authorization_endpoint)
      url.search = new URLSearchParams({ response_type: 'code', client_id: config.client_id, redirect_uri: config.redirect_uri, scope: config.scopes.join(' '), resource: config.resource, code_challenge: challenge, code_challenge_method: 'S256', state }).toString()
      window.location.assign(url)
    } catch (reason: any) {
      setError(reason?.message || String(reason))
      setBusy(false)
    }
  }

  const hasActiveLink = links.some((link) => link.status === 'active')
  const hasAnyActiveLink = hasActiveLink || directConnections.length > 0
  const linkMode = managedOAuthAccountLinkMode({
    hasActiveLink,
    externalAuthId,
    publicClientId: config?.client_id || '',
  })
  const canChooseWorkspace = linkMode === 'provider_completion' || linkMode === 'direct_link'

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f4f7f8', color: '#17252f', padding: 24 }}>
      <main style={{ width: 'min(680px, 100%)', background: '#fff', border: '1px solid #d8e1e5', borderRadius: 16, padding: 30, boxShadow: '0 16px 44px rgba(15,45,58,.10)' }}>
        <div style={{ color: '#1b4965', fontWeight: 800, letterSpacing: 1.5, fontSize: 12 }}>FUSEGRID FINANCIALS</div>
        <h1 style={{ margin: '10px 0 8px', fontSize: 30 }}>Manage AI connectors</h1>
        <p style={{ color: error ? '#991b1b' : '#52636b', lineHeight: 1.55 }}>{error || status}</p>
        <section style={{ marginTop: 22, padding: 20, border: '1px solid #c8d7de', borderRadius: 14, background: 'linear-gradient(145deg, #f8fbfc, #f3f8f6)' }}>
          <div style={{ color: '#1b4965', fontWeight: 850, fontSize: 18 }}>First-time demo setup</div>
          <ol style={{ margin: '13px 0 0', paddingLeft: 22, color: '#40545e', lineHeight: 1.65 }}>
            <li><strong>Install the Excel add-in.</strong> In Excel, choose Home → Add-ins → More Add-ins, search for <strong>FuseGrid CFO Data</strong>, and open it in a new blank workbook. If your organization already deployed it, open <strong>CFO Data</strong> from Excel’s Data ribbon.</li>
            <li><strong>Build the synthetic workbook.</strong> In the FuseGrid pane, choose <strong>Open shared demo</strong>, select a synthetic company, and choose <strong>Build demo workbook</strong>.</li>
            <li><strong>Connect Claude.</strong> Open <a href="https://claude.ai/customize/connectors" target="_blank" rel="noreferrer" style={{ color: '#1b4965', fontWeight: 800 }}>Claude Settings → Connectors</a>, add <strong>FuseGrid CFO Data</strong>, choose Connect, and authorize the shared demo workspace.</li>
            <li><strong>Start the tour.</strong> Return to Excel, choose <strong>Copy guided tour</strong>, paste it into Claude for Excel, and send. Claude will show one capability at a time and pause between steps.</li>
          </ol>
        </section>
        {directConnections.map((connection) => <section key={`${connection.client_id}:${connection.contract_id}`} style={{ marginTop: 22, padding: 18, border: '1px solid #b8d8c8', borderRadius: 14, background: 'linear-gradient(145deg, #f2fbf6, #f8fcfd)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 850, color: '#17613a', fontSize: 18 }}>{connection.client_name}</div>
            <div style={{ color: '#17613a', background: '#dcfce7', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 800 }}>Connected</div>
          </div>
          <div style={{ marginTop: 10, color: '#40545e' }}><strong>Workspace:</strong> {connection.contract}</div>
          <div style={{ marginTop: 5, color: '#5d6e75', fontSize: 13 }}>{connection.workbooks.join(' · ') || 'No client workbooks are currently assigned'}</div>
          <div style={{ marginTop: 8, color: '#718087', fontSize: 12 }}>{connection.last_used_at ? `Last used ${new Date(connection.last_used_at).toLocaleString()}` : `Connected ${new Date(connection.connected_at).toLocaleString()}`}</div>
          <button disabled={busy} onClick={() => void disconnectDirect(connection)} style={{ marginTop: 16, padding: '10px 14px', border: '1px solid #b42318', borderRadius: 9, color: '#b42318', background: '#fff', fontWeight: 800, cursor: 'pointer' }}>{busy ? 'Disconnecting…' : `Disconnect ${connection.client_name}`}</button>
          <p style={{ margin: '12px 0 0', fontSize: 12, color: '#5d6e75' }}>This revokes FuseGrid access immediately. The connector may remain visible in your AI app until you refresh or remove it there.</p>
        </section>)}
        {hasActiveLink ? <section style={{ marginTop: 22, padding: 18, border: '1px solid #b8d8c8', borderRadius: 10, background: '#f2fbf6' }}>
          <div style={{ fontWeight: 800, color: '#17613a' }}>Legacy managed connection</div>
          {links.filter((link) => link.status === 'active').map((link) => <div key={link.id || `${link.contract}-${link.linked_at}`} style={{ marginTop: 8, color: '#40545e' }}>
            <div><strong>Workspace:</strong> {link.contract || 'Authorized workspace'}</div>
            {link.linked_at ? <div style={{ fontSize: 13, marginTop: 3 }}>Connected {new Date(link.linked_at).toLocaleString()}</div> : null}
          </div>)}
          <button disabled={busy} onClick={() => void disconnect()} style={{ marginTop: 16, padding: '10px 14px', border: '1px solid #b42318', borderRadius: 8, color: '#b42318', background: '#fff', fontWeight: 800, cursor: 'pointer' }}>{busy ? 'Disconnecting…' : 'Disconnect FuseGrid connector'}</button>
          <p style={{ margin: '12px 0 0', fontSize: 12, color: '#5d6e75' }}>This immediately revokes FuseGrid data access. The connector may remain visible in your AI app until you refresh or remove it there.</p>
        </section> : null}
        {!hasAnyActiveLink && linkMode === 'provider_start_required' ? <section style={{ marginTop: 22, padding: 18, border: '1px solid #c8d7de', borderRadius: 12, background: '#f7fafb' }}>
          <div style={{ fontWeight: 800, color: '#1b4965' }}>Connect from your AI provider</div>
          <p style={{ margin: '8px 0 0', color: '#52636b', lineHeight: 1.5 }}>Open FuseGrid in Claude or your preferred AI provider and choose Connect. FuseGrid will bring you back to a clear workspace-consent screen; no separate linking URL is needed.</p>
        </section> : null}
        {config?.contracts.length && canChooseWorkspace ? <label style={{ display: 'grid', gap: 8, marginTop: 22, fontWeight: 700 }}>Authorized workspace
          <select value={contractId} onChange={(event) => setContractId(event.target.value)} style={{ padding: 12, border: '1px solid #b8c7ce', borderRadius: 8, background: '#fff' }}>
            <option value="">Select a workspace</option>
            {config.contracts.map((contract) => <option value={contract.id} key={contract.id}>{contract.display_name}</option>)}
          </select>
        </label> : null}
        {config && canChooseWorkspace ? <button disabled={busy || !contractId} onClick={() => void connect()} style={{ marginTop: 20, padding: '12px 18px', border: 0, borderRadius: 8, color: '#fff', background: '#1b4965', fontWeight: 800, cursor: 'pointer' }}>{busy ? 'Authorizing…' : linkMode === 'provider_completion' ? 'Authorize connected app' : 'Connect AI provider'}</button> : null}
        <p style={{ marginTop: 24, fontSize: 12, color: '#718087' }}>FuseGrid stores a hash of the verified provider subject. It does not auto-link an email address or store the provider access token.</p>
      </main>
    </div>
  )
}

function OAuthConsentPage({ requestId }: { requestId: string }) {
  const [details, setDetails] = useState<OAuthConsentDetails | null>(null)
  const [selectedContract, setSelectedContract] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const session = await ensureApiSessionReady()
        if (!session.token) {
          const next = `${window.location.pathname}${window.location.search}`
          window.location.href = `/login?next=${encodeURIComponent(next)}`
          return
        }
        const api = new ServerApiClient()
        const response = await fetch(
          `${api.baseUrl}/api/platform/oauth/authorization/${encodeURIComponent(requestId)}`,
          { headers: { Authorization: `Bearer ${session.token}` }, cache: 'no-store' },
        )
        const body = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(String(body?.error || body?.message || 'This authorization request is no longer available.'))
        if (cancelled) return
        const next = body as OAuthConsentDetails
        setDetails(next)
        if (next.contracts.length === 1) setSelectedContract(next.contracts[0].id)
      } catch (reason: any) {
        if (!cancelled) setError(reason?.message || String(reason))
      }
    }
    void load()
    return () => { cancelled = true }
  }, [requestId])

  const decide = async (approved: boolean) => {
    setBusy(true)
    setError(null)
    try {
      const session = await ensureApiSessionReady()
      if (!session.token) throw new Error('Your FuseGrid session has expired. Sign in and try again.')
      if (approved && !selectedContract) throw new Error('Choose the client access you want to connect.')
      const api = new ServerApiClient()
      const response = await fetch(
        `${api.baseUrl}/api/platform/oauth/authorization/${encodeURIComponent(requestId)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ approved, contract_id: approved ? selectedContract : null }),
          cache: 'no-store',
        },
      )
      const body = await response.json().catch(() => ({}))
      if (!response.ok || !body?.redirect_to) throw new Error(String(body?.error || body?.message || 'FuseGrid could not complete authorization.'))
      window.location.href = String(body.redirect_to)
    } catch (reason: any) {
      setBusy(false)
      setError(reason?.message || String(reason))
    }
  }

  return (
    <main style={{ width: '100%', height: '100dvh', overflowY: 'auto', boxSizing: 'border-box', display: 'grid', placeItems: 'start center', padding: 24, background: 'radial-gradient(circle at top left, #dbeafe 0, #f8fafc 42%, #ecfdf5 100%)', color: '#132b3a', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif', scrollbarGutter: 'stable' }}>
      <section style={{ width: 'min(680px, 100%)', background: 'rgba(255,255,255,.96)', border: '1px solid #d9e4e8', borderRadius: 24, padding: '32px clamp(22px, 5vw, 44px)', boxShadow: '0 24px 70px rgba(24,65,86,.16)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 26 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, display: 'grid', placeItems: 'center', background: '#174f6d', color: 'white', fontFamily: 'Georgia, serif', fontWeight: 800, fontSize: 21 }}>FG</div>
          <div><div style={{ fontWeight: 800, fontSize: 20 }}>Connect FuseGrid to your AI</div><div style={{ color: '#5f7480', marginTop: 3 }}>Governed financial intelligence, scoped to the workspace you choose</div></div>
        </div>
        {error ? <div role="alert" style={{ padding: 14, borderRadius: 12, background: '#fff1f2', color: '#9f1239', marginBottom: 18 }}>{error}</div> : null}
        {!details && !error ? <p style={{ color: '#5f7480' }}>Loading the access request…</p> : null}
        {details ? (
          <>
            <p style={{ fontSize: 18, lineHeight: 1.55, margin: '0 0 20px' }}><strong>{details.client_name}</strong> is asking to use FuseGrid on behalf of <strong>{details.user_email}</strong>.</p>
            <div style={{ padding: 18, borderRadius: 16, background: '#f3f7f8', marginBottom: 22 }}>
              <div style={{ fontWeight: 750, marginBottom: 8 }}>This connection can</div>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#405d6b', lineHeight: 1.7 }}>
                <li>Read evidence-bound financial context and current freshness</li>
                <li>Create governed advisory artifacts and client workspaces</li>
                <li>Update goals and actions only when you explicitly ask your AI to do so</li>
              </ul>
            </div>
            {details.contracts.length ? (
              <fieldset style={{ border: 0, padding: 0, margin: '0 0 24px' }}>
                <legend style={{ fontWeight: 750, marginBottom: 10 }}>Choose client access</legend>
                <div style={{ display: 'grid', gap: 10 }}>
                  {details.contracts.map((contract) => (
                    <label key={contract.id} style={{ display: 'block', padding: 14, border: `2px solid ${selectedContract === contract.id ? '#19765b' : '#d9e4e8'}`, borderRadius: 14, cursor: 'pointer', background: selectedContract === contract.id ? '#ecfdf5' : '#fff' }}>
                      <input type="radio" name="contract" value={contract.id} checked={selectedContract === contract.id} onChange={() => setSelectedContract(contract.id)} style={{ marginRight: 10 }} />
                      <strong>{contract.display_name}</strong>
                      <div style={{ color: '#5f7480', fontSize: 14, margin: '7px 0 0 26px' }}>{contract.workbooks.join(' · ') || 'No client workbooks are currently assigned'}</div>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : <div role="alert" style={{ padding: 14, borderRadius: 12, background: '#fff7ed', color: '#9a3412', marginBottom: 22 }}>Your FuseGrid user does not have an active AI workspace yet.</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <button type="button" disabled={busy} onClick={() => void decide(false)} style={{ padding: '12px 18px', borderRadius: 12, border: '1px solid #b9c8cf', background: '#fff', color: '#294b5c', fontWeight: 750, cursor: 'pointer' }}>Cancel</button>
              <button type="button" disabled={busy || !selectedContract} onClick={() => void decide(true)} style={{ padding: '12px 20px', borderRadius: 12, border: 0, background: busy || !selectedContract ? '#9fb4bd' : '#174f6d', color: '#fff', fontWeight: 800, cursor: busy || !selectedContract ? 'not-allowed' : 'pointer' }}>{busy ? 'Connecting…' : `Connect ${details.client_name}`}</button>
            </div>
            <p style={{ color: '#71858f', fontSize: 13, lineHeight: 1.5, margin: '22px 0 0' }}>Your AI provider receives revocable, workspace-scoped access. It never receives your FuseGrid password or unrestricted database access.</p>
          </>
        ) : null}
      </section>
    </main>
  )
}

function DevLoginBridgePage({ mode }: { mode: 'issue' | 'redeem' }) {
  const [status, setStatus] = useState('Preparing dev login bridge...')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const sp = new URLSearchParams(window.location.search || '')
        const api = new ServerApiClient()
        if (mode === 'issue') {
          setStatus('Creating a short-lived localhost login bridge...')
          const session = await ensureApiSessionReady()
          if (!session.token) {
            throw new Error('Sign in on this production domain first, then reopen the dev login bridge URL.')
          }
          const targetOrigin = String(sp.get('target') || sp.get('target_origin') || 'http://127.0.0.1:5180').trim()
          const redirectPath = String(sp.get('next') || '/?view=tableau-creator').trim()
          const bridge = await api.createDevLoginBridge({
            target_origin: targetOrigin,
            redirect_path: redirectPath,
          })
          if (cancelled) return
          setStatus('Redirecting to localhost with the one-time bridge code...')
          window.location.href = bridge.bridge_url
          return
        }
        const code = String(sp.get('code') || '').trim()
        if (!code) throw new Error('Missing dev login bridge code.')
        setStatus('Redeeming bridge code into a localhost session...')
        await api.redeemDevLoginBridge(code, window.location.origin)
        if (cancelled) return
        const next = String(sp.get('next') || '/?view=tableau-creator').trim()
        window.location.href = next.startsWith('/') && !next.startsWith('//') ? next : '/?view=tableau-creator'
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || String(e))
          setStatus('Dev login bridge failed.')
        }
      }
    }
    void run()
    return () => { cancelled = true }
  }, [mode])

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f8fafc', color: '#111827', padding: 24 }}>
      <div style={{ width: 'min(680px, 100%)', border: '1px solid #d1d5db', borderRadius: 14, padding: 28, background: '#ffffff', boxShadow: '0 10px 30px rgba(15,23,42,0.08)' }}>
        <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 10 }}>Dev Login Bridge</div>
        <div style={{ fontSize: 15, color: error ? '#991b1b' : '#4b5563', lineHeight: 1.5 }}>{error || status}</div>
        {mode === 'issue' ? (
          <div style={{ marginTop: 14, fontSize: 12, color: '#6b7280' }}>
            This creates a three-minute, single-use localhost session bridge from your authenticated production session.
          </div>
        ) : null}
      </div>
    </div>
  )
}

function redirectToLoginForInvalidSession(loc: Location | null | undefined): boolean {
  if (typeof window === 'undefined' || !loc) return false
  if (isHybridReportLaunchLocation(loc)) return false
  const routeMode = readAppRouteMode(loc)
  const currentPath = `${loc.pathname || '/'}${loc.search || ''}${loc.hash || ''}`
  if (!currentPath || currentPath.startsWith('/login') || currentPath.startsWith('/auth/')) return false
  if (currentPath.startsWith('/share/cfo/')) return false
  if (
    routeMode === 'cfo_landing' ||
    routeMode === 'cfo_pricing' ||
    routeMode === 'cfo_signup' ||
    routeMode === 'cfo_demo_entry' ||
    routeMode === 'cfo_onboarding' ||
    routeMode === 'billing_checkout_complete' ||
    routeMode === 'cfo_legal_privacy' ||
    routeMode === 'cfo_legal_terms'
  ) {
    return false
  }
  const nextPath = currentPath.startsWith('/') && !currentPath.startsWith('//') ? currentPath : '/'
  const loginPath = `/login?next=${encodeURIComponent(nextPath)}`
  window.history.replaceState({}, '', loginPath)
  try { window.dispatchEvent(new Event('fg_route_change')) } catch { }
  return true
}

function DevUrlBar() {
  const [value, setValue] = useState<string>(() => {
    try { return String(window.location.href || '') } catch { return '' }
  })
  const [showLogs, setShowLogs] = useState(false)
  const [logTick, setLogTick] = useState(0)

  useEffect(() => {
    const onPop = () => {
      try { setValue(String(window.location.href || '')) } catch { }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => {
    // Dev-only: capture console output into an in-app panel.
    const w: any = window as any
    if (w.__FG_CONSOLE_TAP_INSTALLED__) return
    w.__FG_CONSOLE_TAP_INSTALLED__ = true
    if (!w.__FG_CONSOLE_BUFFER__) w.__FG_CONSOLE_BUFFER__ = []
    const buf: FgConsoleEntry[] = w.__FG_CONSOLE_BUFFER__
    const max = 600
    let scheduled = false
    const emit = () => {
      // Important: do NOT synchronously trigger React state updates during render.
      // Console logging can occur while React is rendering; schedule the notification.
      if (scheduled) return
      scheduled = true
      const fire = () => {
        scheduled = false
        try { window.dispatchEvent(new Event('fg_console_entry')) } catch { }
      }
      try {
        if (typeof queueMicrotask === 'function') queueMicrotask(fire)
        else window.setTimeout(fire, 0)
      } catch {
        try { window.setTimeout(fire, 0) } catch { }
      }
    }
    const wrap = (level: FgConsoleEntry['level']) => {
      const orig = (console as any)[level]
      if (typeof orig !== 'function') return
      ;(console as any)[level] = (...args: any[]) => {
        try {
          const ts = Date.now()
          const text = formatConsoleArgs(args)
          buf.push({ ts, level, text })
          if (buf.length > max) buf.splice(0, buf.length - max)
          emit()
        } catch { }
        return orig.apply(console, args as any)
      }
    }
    wrap('log')
    wrap('info')
    wrap('warn')
    wrap('error')
    wrap('debug')
  }, [])

  useEffect(() => {
    const on = () => setLogTick((t) => t + 1)
    window.addEventListener('fg_console_entry', on as any)
    return () => window.removeEventListener('fg_console_entry', on as any)
  }, [])

  const logs: FgConsoleEntry[] = (() => {
    try {
      const w: any = window as any
      const buf = w.__FG_CONSOLE_BUFFER__
      return Array.isArray(buf) ? (buf as FgConsoleEntry[]) : []
    } catch {
      return []
    }
  })()

  const go = () => {
    const raw = String(value || '').trim()
    if (!raw) return
    try {
      const next =
        raw.startsWith('http://') || raw.startsWith('https://')
          ? raw
          : raw.startsWith('?')
            ? `${window.location.pathname}${raw}`
            : raw.startsWith('/')
              ? raw
              : `${window.location.pathname}?${raw}`
      window.location.href = next
    } catch {
      // ignore
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50_000,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        padding: '6px 8px',
        background: 'rgba(17, 24, 39, 0.92)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        color: '#fff',
        fontSize: 12,
        height: DEV_URL_BAR_HEIGHT_PX,
        minHeight: DEV_URL_BAR_HEIGHT_PX,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ fontWeight: 900, opacity: 0.9 }}>Dev URL</div>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') go()
          if (e.key === 'Escape') {
            try { setValue(String(window.location.href || '')) } catch { }
          }
        }}
        spellCheck={false}
        style={{
          flex: 1,
          minWidth: 220,
          padding: '6px 8px',
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(255,255,255,0.08)',
          color: '#fff',
          outline: 'none',
        }}
      />
      <button
        onClick={go}
        style={{
          padding: '6px 10px',
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.22)',
          background: 'rgba(255,255,255,0.12)',
          color: '#fff',
          fontWeight: 900,
          cursor: 'pointer',
        }}
        title="Navigate (reloads)"
      >
        Go
      </button>
      <button
        onClick={() => setShowLogs((v) => !v)}
        style={{
          padding: '6px 10px',
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.22)',
          background: showLogs ? 'rgba(59, 130, 246, 0.35)' : 'rgba(255,255,255,0.12)',
          color: '#fff',
          fontWeight: 900,
          cursor: 'pointer',
        }}
        title="Toggle in-app console log panel"
      >
        Logs{logs.length ? ` (${logs.length})` : ''}
      </button>
      {showLogs ? (
        <div
          style={{
            position: 'fixed',
            top: DEV_URL_BAR_HEIGHT_PX,
            left: 0,
            right: 0,
            height: '42vh',
            zIndex: 49_999,
            background: 'rgba(0,0,0,0.92)',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            color: '#e5e7eb',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: 11,
            overflow: 'auto',
            padding: 10,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontWeight: 900, opacity: 0.9 }}>Console logs</div>
            <button
              onClick={async () => {
                try {
                  const text = logs
                    .map((e) => {
                      const t = new Date(e.ts).toISOString()
                      return `${t} [${e.level}] ${e.text}`
                    })
                    .join('\n')
                  await navigator.clipboard.writeText(text)
                } catch { }
              }}
              style={{
                padding: '4px 8px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.22)',
                background: 'rgba(255,255,255,0.10)',
                color: '#fff',
                fontWeight: 900,
                cursor: 'pointer',
              }}
            >
              Copy
            </button>
            <button
              onClick={() => {
                try {
                  const w: any = window as any
                  if (Array.isArray(w.__FG_CONSOLE_BUFFER__)) w.__FG_CONSOLE_BUFFER__.splice(0, w.__FG_CONSOLE_BUFFER__.length)
                  setLogTick((t) => t + 1)
                } catch { }
              }}
              style={{
                padding: '4px 8px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.22)',
                background: 'rgba(255,255,255,0.10)',
                color: '#fff',
                fontWeight: 900,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
            <div style={{ marginLeft: 'auto', opacity: 0.8 }}>lines: {logs.length} (tick {logTick})</div>
          </div>
          {logs.length ? (
            <div>
              {logs
                .slice(Math.max(0, logs.length - 600))
                .map((e, idx) => (
                  <div key={`${e.ts}-${idx}`} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 2 }}>
                    <span style={{ opacity: 0.7 }}>{new Date(e.ts).toISOString()}</span>{' '}
                    <span style={{ opacity: 0.8 }}>[{e.level}]</span>{' '}
                    <span>{e.text}</span>
                  </div>
                ))}
            </div>
          ) : (
            <div style={{ opacity: 0.75 }}>No logs captured yet.</div>
          )}
        </div>
      ) : null}
    </div>
  )
}

function DevImpersonationBanner({
  session,
  topOffsetPx,
}: {
  session: DevImpersonationSession | null
  topOffsetPx: number
}) {
  if (!session) return null
  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: topOffsetPx,
        height: DEV_IMPERSONATION_BAR_HEIGHT_PX,
        zIndex: 2147483646,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '0 12px',
        boxSizing: 'border-box',
        background: '#111827',
        color: '#ffffff',
        borderBottom: '1px solid rgba(255,255,255,0.16)',
        fontSize: 12,
      }}
    >
      <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        Viewing as {session.targetEmail || session.targetUserId}
      </div>
      <button
        style={{
          border: '1px solid rgba(255,255,255,0.28)',
          background: '#ffffff',
          color: '#111827',
          borderRadius: 7,
          padding: '5px 10px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          flex: '0 0 auto',
        }}
        onClick={() => {
          if (restoreDevImpersonationSession()) {
            window.location.href = '/admin/users'
          }
        }}
      >
        Return to admin
      </button>
    </div>
  )
}

function CfoDemoHomeRedirect({ path }: { path: string }) {
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.location.replace(path)
  }, [path])

  return null
}

function App() {
  const apiSession = useApiSessionSnapshot()
  const [activityApi] = useState(() => new ServerApiClient())
  useRouteActivityTelemetry()

  useEffect(() => {
    installGlobalActivityTelemetry()
    initAnalytics({ transport: activityApi })
  }, [activityApi, apiSession.token])

  const normalizeWorkbookId = (raw: any): string => {
    const s = String(raw || '').trim()
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
    return isUuid ? s : ''
  }

  const [controller] = useState(() => new GridController());
  const startOnQueryTabRanRef = useRef(false);
  const queryBuilderDebug = (() => {
    if (!import.meta.env.DEV) return false;
    const sp = new URLSearchParams(window.location.search);
    const truthy = (v: any) => ['1', 'true', 'yes', 'on'].includes(String(v ?? '').trim().toLowerCase());
    const v = (k: string) => truthy(sp.get(k));
    if (v('query_builder_debug') || v('query_debug') || v('qb_debug') || v('fg_debug_qb')) return true;
    try {
      if (truthy(window.localStorage.getItem('fg_debug_qb'))) return true;
    } catch { }
    return false;
  })();
  const fieldMappingDebug = isFieldMappingDebugEnabled();
  const fieldMappingConsoleDebug = isFieldMappingConsoleEnabled();
  const vizPointsDebug = (() => {
    if (!import.meta.env.DEV) return false;
    const sp = new URLSearchParams(window.location.search);
    const v = (k: string) => (sp.get(k) === '1') || (sp.get(k) === 'true');
    return (
      v('debug_viz_points') ||
      v('viz_points_debug') ||
      v('debug_viz') ||
      v('viz_debug')
    );
  })();
  const executionCacheDebug = (() => {
    if (!import.meta.env.DEV) return false;
    const sp = new URLSearchParams(window.location.search);
    const truthy = (v: any) => ['1', 'true', 'yes', 'on'].includes(String(v ?? '').trim().toLowerCase());
    const v = (k: string) => truthy(sp.get(k));
    if (
      v('fg_debug_execution_cache') ||
      v('fg_debug_exec_cache') ||
      v('debug_execution_cache') ||
      v('debug_exec_cache')
    ) return true;
    try {
      if (truthy(window.localStorage.getItem('fg_debug_execution_cache'))) return true;
      if (truthy(window.localStorage.getItem('fg_debug_exec_cache'))) return true;
    } catch { }
    return false;
  })();
  const canonicalWarmupArbiterFlag = (() => {
    const sp = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const truthy = (v: any) => ['1', 'true', 'yes', 'on'].includes(String(v ?? '').trim().toLowerCase());
    const falsy = (v: any) => ['0', 'false', 'no', 'off'].includes(String(v ?? '').trim().toLowerCase());
    const v = (k: string) => truthy(sp?.get(k));
    const raw = sp?.get('fg_canonical_warmup_arbiter') ?? '';
    if (raw) return truthy(raw);
    if (v('fg_canonical_warmup_arbiter')) return true;
    try {
      if (typeof window !== 'undefined') {
        const stored = window.localStorage.getItem('fg_canonical_warmup_arbiter') ?? '';
        if (stored) {
          if (falsy(stored)) return false;
          if (truthy(stored)) return true;
        }
      }
    } catch { }
    try { return Boolean(import.meta?.env?.DEV); } catch { return false; }
  })();

  // Phase 3a: dataset-level shaped-cache budget. Default on for local dev (Vite)
  // and off in prod until Phase 3b gates pass. Users can still toggle via URL or
  // localStorage.
  const datasetShapeBudgetFlag = (() => {
    const sp = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const truthy = (v: any) => ['1', 'true', 'yes', 'on'].includes(String(v ?? '').trim().toLowerCase());
    const falsy = (v: any) => ['0', 'false', 'no', 'off'].includes(String(v ?? '').trim().toLowerCase());
    const sv = sp?.get('fg_dataset_shape_budget') ?? '';
    if (sv) return truthy(sv);
    try {
      if (typeof window !== 'undefined') {
        const ls = window.localStorage.getItem('fg_dataset_shape_budget') ?? '';
        if (ls) {
          if (falsy(ls)) return false;
          if (truthy(ls)) return true;
        }
      }
    } catch { }
    try { return Boolean(import.meta?.env?.DEV); } catch { return false; }
  })();
  const datasetShapeBudgetBytes = (() => {
    const sp = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const parse = (raw: any) => {
      const s = String(raw ?? '').trim();
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
    };
    const qp = parse(sp?.get('fg_dataset_shape_budget_bytes'));
    if (qp != null) return qp;
    try {
      if (typeof window !== 'undefined') {
        const ls = parse(window.localStorage.getItem('fg_dataset_shape_budget_bytes'));
        if (ls != null) return ls;
      }
    } catch { }
    return null;
  })();

  // PR6 cutover diagnostic flag: `?fg_use_ts_legacy_compiler=1` or
  // `localStorage.fg_use_ts_legacy_compiler=1` forces the worker to skip
  // the Rust compiler install, but strict `typed-required` mode still
  // fails closed before TS legacy can compile. The default (flag off,
  // flag absent, or flag set to a falsy value) installs Rust.
  const useTsLegacyCompilerFlag = shouldUseTsLegacyCompilerKillSwitch()

  const showDevUrlBar = import.meta.env.DEV && isFuseGridNativeDesktopShell()
  const [devImpersonation, setDevImpersonation] = useState<DevImpersonationSession | null>(() => readDevImpersonationSession())
  const devUrlBarInsetPx = showDevUrlBar ? DEV_URL_BAR_HEIGHT_PX : 0
  const topInsetPx = devUrlBarInsetPx + (devImpersonation ? DEV_IMPERSONATION_BAR_HEIGHT_PX : 0)
  const [appRouteMode, setAppRouteMode] = useState<AppRouteMode>(() => {
    if (typeof window === 'undefined') return 'admin_shell'
    return readAppRouteMode(window.location)
  })
  const [appRouteVersion, setAppRouteVersion] = useState(0)
  const shouldStartAppWorker = useMemo(() => {
    if (appRouteMode === 'workbook') return true
    if (appRouteMode !== 'cfo_core' || typeof window === 'undefined') return false
    return cfoRouteNeedsAppWorker(window.location)
  }, [appRouteMode, appRouteVersion])

  useEffect(() => {
    setDevImpersonation(readDevImpersonationSession())
    return subscribeDevImpersonationSession(() => {
      setDevImpersonation(readDevImpersonationSession())
    })
  }, [])

  const renderTopChrome = () => (
    <>
      {showDevUrlBar && <DevUrlBar />}
      <DevImpersonationBanner session={devImpersonation} topOffsetPx={devUrlBarInsetPx} />
    </>
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const syncRouteMode = () => {
      setAppRouteMode(readAppRouteMode(window.location))
      setAppRouteVersion((version) => version + 1)
    }
    window.addEventListener('popstate', syncRouteMode)
    window.addEventListener('fg_route_change', syncRouteMode as EventListener)
    return () => {
      window.removeEventListener('popstate', syncRouteMode)
      window.removeEventListener('fg_route_change', syncRouteMode as EventListener)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onAuthInvalid = () => {
      redirectToLoginForInvalidSession(window.location)
    }
    window.addEventListener('fusegrid_api_auth_invalid', onAuthInvalid as EventListener)
    return () => {
      window.removeEventListener('fusegrid_api_auth_invalid', onAuthInvalid as EventListener)
    }
  }, [])

  useEffect(() => {
    if (appRouteMode !== 'workbook') return
    void ensureMainThreadWorksheetCompilerBootstrapped()
  }, [appRouteMode])

  // App infrastructure
  const [infra, setInfra] = useState<{
    bus: WorkerBus,
    client: AppWorkerClient,
    tabManager: TabManager,
    streamer: ViewportStreamer
  } | null>(null);

  // Workbook sync (spreadsheet ops over DocumentSession).
  const workbookSessionRef = useRef<WorkbookSessionFacade | null>(null);
  const [selectedWorkbookId, setSelectedWorkbookId] = useState<string>(() => {
    if (typeof window === 'undefined') return ''
    const sp = new URLSearchParams(window.location.search)
    const q = normalizeWorkbookId(sp.get('workbook_id'))
    if (q) return q
    return normalizeWorkbookId(window.localStorage.getItem('fusegrid_workbook_id'))
  })
  const [collabEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    const stored = String(window.localStorage.getItem('fusegrid_collab_enabled') || '').trim()
    if (stored === '0' || stored === 'false') return false
    if (stored === '1' || stored === 'true') return true
    // default: enabled only if a workbook id exists
    const wid = String(window.localStorage.getItem('fusegrid_workbook_id') || '').trim()
    return !!wid
  })
  const [, setCollabStatus] = useState<CollabStatus>('disconnected')
  const [, setCollabError] = useState<string | null>(null)
  const [, setCollabHeadSeq] = useState<number | null>(null)
  const [, setCollabPendingOps] = useState<number>(0)
  const [, setCollabWorkbookId] = useState<string>(() => (typeof window === 'undefined') ? '' : String(window.localStorage.getItem('fusegrid_workbook_id') || '').trim())
  const [, setWorkbookName] = useState<string>('')
  const [, setCollabBoot] = useState<{ total: number; done: number } | null>(null)
  const [, setTabUpdateCounts] = useState<Record<string, number>>({})

  // Tab State
  const [tabs, setTabs] = useState<TabSummary[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  // Grid Dimensions
  const [colCount] = useState(26);
  const [rowCount, setRowCount] = useState(1000);

  // Cache Status
  const [, setCacheStatus] = useState<{ status: string, progress: number } | null>(null);
  const [, setDocSaveUi] = useState<{ status: 'idle' | 'loading' | 'saved' | 'saving' | 'error' | 'conflict'; message?: string | null }>({ status: 'idle', message: null })

  // Dashboard debug: log resource load failures (e.g. broken <img>) and unhandled promise rejections.
  // Enable by appending `?dashboard_debug=1` to the URL.
  useEffect(() => {
    void ensureApiSessionReady()
  }, [])

  // Request persistent storage on mount. Origins granted "persistent" storage
  // get a larger OPFS/IndexedDB quota (typically up to ~60% of free disk in
  // Chrome) and their data is protected from opportunistic eviction. DuckDB's
  // execution-scope cache materializes large `aggregate_base` TEMP TABLEs to
  // OPFS and can trivially exceed the default ~3 GiB quota on some origins.
  // Calling `navigator.storage.persist()` from the main thread (where a user
  // gesture is available) is the only way to unlock that larger quota.
  useEffect(() => {
    void requestPersistentStorage()
      .then((granted) => {
        try {
          console.log('[FG-STORAGE] persist()', JSON.stringify({ granted }))
        } catch { /* ignore */ }
      })
      .catch(() => { /* best-effort */ })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const sp = new URLSearchParams(window.location.search)
    const v = String(sp.get('dashboard_debug') || '').trim().toLowerCase()
    const enabled = v === '1' || v === 'true' || v === 'yes'
    if (!enabled) return

    const onErr = (ev: any) => {
      try {
        const t = ev?.target as any
        // Resource errors (img/script/link) come through with a target element.
        if (t && typeof t === 'object' && t.tagName) {
          const tag = String(t.tagName || '').toUpperCase()
          const src = (t as any).currentSrc || (t as any).src || (t as any).href || null
          console.warn('[FG-DASH] window.onerror (resource) ' + JSON.stringify({ tag, src }))
          return
        }
        const msg = String(ev?.message || ev?.error?.message || ev?.error || 'unknown_error')
        console.warn('[FG-DASH] window.onerror ' + JSON.stringify({ message: msg }))
      } catch { }
    }

    const onRej = (ev: any) => {
      try {
        const reason = ev?.reason
        const msg = reason instanceof Error ? reason.message : String(reason)
        console.warn('[FG-DASH] unhandledrejection ' + JSON.stringify({ message: msg }))
      } catch { }
    }

    window.addEventListener('error', onErr as any, true)
    window.addEventListener('unhandledrejection', onRej as any)
    console.log('[FG-DASH] dashboard_debug enabled')
    return () => {
      window.removeEventListener('error', onErr as any, true)
      window.removeEventListener('unhandledrejection', onRej as any)
    }
  }, [])

  useEffect(() => {
    const onDoc = (ev: any) => {
      const s = String(ev?.detail?.status || 'idle') as any
      const m = ev?.detail?.message ?? null
      setDocSaveUi({ status: s, message: m })
    }
    window.addEventListener('fusegrid_workbook_doc_status', onDoc as any)
    return () => window.removeEventListener('fusegrid_workbook_doc_status', onDoc as any)
  }, [])

  // Fetch workbook name for the selected workbook (used in both sheet + query UIs).
  useEffect(() => {
    if (appRouteMode !== 'workbook') {
      setWorkbookName('')
      return
    }
    if (typeof window === 'undefined') return
    const wid = String(selectedWorkbookId || '').trim()
    if (!wid) {
      setWorkbookName('')
      return
    }
    if (!apiSession.initialized || !apiSession.token) {
      setWorkbookName('')
      return
    }
    let cancelled = false
    const api = new ServerApiClient()
      ; (async () => {
        try {
          const wb = await api.getWorkbook(wid)
          if (cancelled) return
          setWorkbookName(String(wb?.name || '').trim())
        } catch (e: any) {
          if (cancelled) return
          if (e?.status === 404) {
            try { window.localStorage.removeItem('fusegrid_workbook_id') } catch { }
            setSelectedWorkbookId('')
            setCollabWorkbookId('')
          }
          setWorkbookName('')
        }
      })()
    return () => { cancelled = true }
  }, [apiSession.initialized, apiSession.token, appRouteMode, selectedWorkbookId])

  // Normalize workbook selection: URL ?workbook_id wins, and we always persist to localStorage.
  useEffect(() => {
    if (appRouteMode !== 'workbook') return
    const sp = new URLSearchParams(window.location.search)
    const qRaw = sp.get('workbook_id')
    const q = normalizeWorkbookId(qRaw)
    if (q) {
      try { window.localStorage.setItem('fusegrid_workbook_id', q) } catch { }
      setSelectedWorkbookId(q)
      setCollabWorkbookId(q)
      return
    }
    const storedRaw = window.localStorage.getItem('fusegrid_workbook_id')
    const stored = normalizeWorkbookId(storedRaw)
    if (stored) {
      setSelectedWorkbookId(stored)
      setCollabWorkbookId(stored)
      return
    }
    // If an invalid workbook id is present (common source of 400s), clear it so the user can re-open cleanly.
    const hadInvalid = Boolean(String(qRaw || '').trim() || String(storedRaw || '').trim())
    if (hadInvalid) {
      try { window.localStorage.removeItem('fusegrid_workbook_id') } catch { }
      setSelectedWorkbookId('')
      setCollabWorkbookId('')
    }
  }, [appRouteMode])

  // Whole-app UI zoom (persisted). This is separate from sheet/grid zoom.
  const uiZoomRef = useRef<number>(1);
  useEffect(() => {
    const key = 'fusegrid_ui_scale'
    const clamp = (n: number) => Math.max(0.5, Math.min(2.0, n))
    const isCreatorRoute = () => isTableauCreatorRoute(window.location)
    const read = () => {
      const raw = window.localStorage.getItem(key) || ''
      const n = Number(raw)
      if (Number.isFinite(n) && n > 0) return clamp(n)
      return 1
    }
    const apply = (n: number) => {
      const v = clamp(n)
      const creatorRoute = isCreatorRoute()
      const applied = creatorRoute ? 1 : v
      const inverse = Number.isFinite(applied) && applied !== 0 ? (1 / applied) : 1
      const nextSignature = `${window.location.pathname}::${applied}::${inverse}`
      if (
        lastUiZoomApplySignature === nextSignature &&
        document.documentElement.style.getPropertyValue('--fg-ui-scale') === String(applied) &&
        document.documentElement.style.getPropertyValue('--fg-ui-scale-inverse') === String(inverse)
      ) {
        uiZoomRef.current = applied
        return
      }
      lastUiZoomApplySignature = nextSignature
      uiZoomRef.current = applied
      document.documentElement.style.setProperty('--fg-ui-scale', String(applied))
      document.documentElement.style.setProperty('--fg-ui-scale-inverse', String(inverse))
      // Only set `zoom` when actually scaling. `zoom: 1` is a no-op visually but
      // its presence makes Chrome render native <select> popups oversized and
      // detached, so clear it at scale 1.
      try { document.body.style.zoom = applied !== 1 ? String(applied) : '' } catch { }
      if (!creatorRoute) {
        window.localStorage.setItem(key, String(v))
      }
      if (import.meta.env.DEV) {
        console.debug('[ui-zoom] set', { requestedScale: v, appliedScale: applied, inverse, route: window.location.pathname, viewport: { w: window.innerWidth, h: window.innerHeight } })
      }
    }
    apply(read())

    const isEditableTarget = (t: EventTarget | null) => {
      if (!t || !(t as any).closest) return false
      const el = t as HTMLElement
      return !!el.closest('input, textarea, [contenteditable="true"]')
    }

    const onKeyDown = (e: KeyboardEvent) => {
      const isCmd = e.metaKey || e.ctrlKey
      if (!isCmd) return
      if (isCreatorRoute()) return
      if (isEditableTarget(e.target)) return
      // Normalize: on many layouts '+' is Shift+'='
      const k = String(e.key || '')
      const wantsIn = (k === '+' || k === '=')
      const wantsOut = (k === '-' || k === '_')
      const wantsReset = (k === '0')
      if (!wantsIn && !wantsOut && !wantsReset) return

      e.preventDefault()
      e.stopPropagation()

      if (wantsReset) {
        apply(1)
        return
      }
      const step = e.shiftKey ? 0.2 : 0.1
      if (wantsIn) apply(uiZoomRef.current + step)
      else if (wantsOut) apply(uiZoomRef.current - step)
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true } as any)
  }, [])

  useEffect(() => {
    // Prevent default scroll behavior
    document.body.style.overflow = 'hidden';
    installDiagnostics();

    if (!shouldStartAppWorker) {
      setInfra(null)
      setTabs([])
      setActiveTabId(null)
      return
    }

    if ((fieldMappingDebug || queryBuilderDebug) && typeof window !== 'undefined') {
      try {
        ; (window as any).__FG_DEBUG_FIELD_MAPPING__ = true;
        ; (window as any).__FG_DEBUG_FIELD_MAPPING_CONSOLE__ = fieldMappingConsoleDebug || queryBuilderDebug;
        if (queryBuilderDebug) {
          console.log('[FG-QB-DEBUG] enabled');
        }
        console.log('[FG-FIELD-MAP] debug enabled');
      } catch { }
    }

    if (USE_WORKER) {
      const worker = new FuseWorker();
      const bus = new WorkerBus(worker);
      const streamer = new ViewportStreamer(bus);
      const client = new AppWorkerClient(bus);
      const isCreatorRoute = (() => {
        if (typeof window === 'undefined') return false
        try {
          return isTableauCreatorRoute(window.location)
        } catch {
          return false
        }
      })()

      // Connect everything
      // @ts-ignore
      const sab = SharedState.create(26, 1000); // Default
      // @ts-ignore
      (controller as any).setSharedState(sab);
      controller.setDimensions(26, 1000);
      // In Tableau Creator, SpreadsheetSheetCanvas owns its own streamer instance.
      // Attaching the App-level streamer here can issue competing VIEWPORT_UPDATE calls.
      if (!isCreatorRoute) {
        controller.setStreamer(streamer);
      }

      // Capture worker errors
      worker.addEventListener('error', (e: Event) => {
        console.error('[App] Worker error', e);
      });

      // Cache Subscriptions
      const unsubCache = bus.subscribe('CACHE_UPDATE', (payload: any) => {
        setCacheStatus(payload);
      });

      // Diagnostics: confirm threaded/shared-memory WASM is actually running with SAB-backed memory.
      const unsubWasmInfo = bus.subscribe('WASM_INFO', (payload: any) => {
        console.log('[App] WASM_INFO', payload);
      });

      // Wait for worker readiness before making RPC calls (prevents list_tabs on null app)
      let tabManager: TabManager | null = null;
      let unsubTabs: (() => void) | null = null;
      const unsubReady = bus.subscribe('READY', () => {
        if (tabManager) return; // already initialized
        tabManager = new TabManager(client, bus);
        unsubTabs = tabManager.subscribe(() => {
          setTabs(tabManager!.getTabs());
          setActiveTabId(tabManager!.getActiveTabId());
        });
        setInfra({ bus, client, tabManager, streamer });
      });
      const unsubFieldTrace = bus.subscribe('FIELD_MAPPING_TRACE', (payload: any) => {
        try {
          if (!payload) return;
          pushFieldMappingTrace(payload);
        } catch { }
      });

      // Init worker (after wiring subscriptions)
      bus.send('INIT', {
        cols: 26,
        rows: 1000,
        sab,
        desktop: isFuseGridNativeDesktopShell(),
        debug: {
          field_mapping: fieldMappingDebug,
          field_mapping_console: fieldMappingConsoleDebug,
          viz_points: vizPointsDebug,
          execution_cache: executionCacheDebug,
        },
        flags: {
          canonical_warmup_arbiter: canonicalWarmupArbiterFlag,
          dataset_shape_budget: datasetShapeBudgetFlag,
          dataset_shape_budget_bytes: datasetShapeBudgetBytes,
          use_ts_legacy_compiler: useTsLegacyCompilerFlag,
        },
      });

      // Best-effort: ask worker to close native DuckDB sessions on reload/unload.
      // Without this, Electron reloads can leak sessions in the native sidecar and cause lock/contention.
      const onBeforeUnload = () => {
        try { bus.send('SHUTDOWN', {}); } catch { }
      }
      try { window.addEventListener('beforeunload', onBeforeUnload); } catch { }

      return () => {
        try { window.removeEventListener('beforeunload', onBeforeUnload); } catch { }
        try { bus.send('SHUTDOWN', {}); } catch { }
        unsubReady();
        unsubTabs?.();
        unsubCache();
        unsubWasmInfo();
        unsubFieldTrace();
        setInfra(null)
        setTabs([])
        setActiveTabId(null)
        worker.terminate();
      };
    } else {
      // Local-only mode
      controller.setDimensions(colCount, rowCount);
      controller.setRowCountChangeCallback((newCount) => setRowCount(newCount));
    }
  }, [controller, executionCacheDebug, fieldMappingConsoleDebug, fieldMappingDebug, queryBuilderDebug, shouldStartAppWorker, vizPointsDebug]); // setup only when a route needs the worker

  // Wire spreadsheet sync once infra is ready and we have token + workbook id (gated by UI toggle).
  useEffect(() => {
    if (appRouteMode !== 'workbook') return;
    if (!infra) return;
    if (typeof window === 'undefined') return;

    const workbookId = String(window.localStorage.getItem('fusegrid_workbook_id') || '').trim();
    setCollabWorkbookId(workbookId)
    if (!apiSession.initialized || !apiSession.token || !workbookId) return;
    if (!collabEnabled) return;

    const api = new ServerApiClient();
    const session = new WorkbookSessionFacade(api as any, workbookId);
    workbookSessionRef.current = session;
    const appliedOperationIds = new Set<string>()
    let cancelled = false

    const applyDocumentSessionOperation = (operation: SpreadsheetEngineOperationEnvelope) => {
      const opId = String(operation?.op_id || '').trim()
      if (!opId || appliedOperationIds.has(opId)) return
      const sheetKey = String(operation?.sheet_id || '').trim()
      const m = sheetKey.match(/^sheet_(\d+)$/)
      const sheetNum = m ? Number(m[1]) : NaN
      if (!Number.isFinite(sheetNum)) return
      const activeSheetId = infra.streamer.getActiveSheetId() ?? 1
      if (sheetNum !== activeSheetId) {
        const tabId = `sheet-${sheetNum}`
        setTabUpdateCounts(prev => ({ ...prev, [tabId]: (prev[tabId] || 0) + 1 }))
      }
      appliedOperationIds.add(opId)
      infra.streamer.applyRemoteSpreadsheetEngineOperation(operation)
    }

    const unsubscribeReplica = session.subscribeReplicaChange((snapshot) => {
      if (cancelled) return
      try {
        setCollabHeadSeq(snapshot?.head_op_seq ?? null)
        for (const operation of session.readSpreadsheetEngineOperations()) {
          applyDocumentSessionOperation(operation)
        }
      } catch { }
    })

    infra.streamer.setAcceptedOperationsCallback((operations) => {
      const durableOps = (Array.isArray(operations) ? operations : [])
        .filter((operation) => {
          const opId = String(operation?.op_id || '').trim()
          const sheetId = String(operation?.sheet_id || '').trim()
          return Boolean(opId && sheetId && !appliedOperationIds.has(opId))
        })
      if (durableOps.length <= 0) return
      const updateIds = session.submitSpreadsheetEngineOperations(durableOps)
      if (updateIds.length > 0) {
        for (const operation of durableOps) {
          appliedOperationIds.add(String(operation.op_id || '').trim())
        }
      }
      setCollabPendingOps(updateIds.length)
    })

    setCollabStatus('connecting')
    setCollabBoot({ total: 1, done: 0 })
    void session.bootstrap()
      .then((snapshot) => {
        if (cancelled) return
        setCollabHeadSeq(snapshot.head_op_seq)
        setCollabStatus('connected')
        setCollabBoot({ total: 1, done: 1 })
        session.connect()
      })
      .catch((e: any) => {
        if (cancelled) return
        setCollabStatus('error')
        setCollabError(e?.message || String(e))
      })
      .finally(() => {
        if (cancelled) return
        setTimeout(() => setCollabBoot(null), 1000)
      })

    return () => {
      cancelled = true
      infra.streamer.setAcceptedOperationsCallback(null);
      try { unsubscribeReplica() } catch { }
      session.disconnect();
      setCollabStatus('disconnected')
      if (workbookSessionRef.current === session) workbookSessionRef.current = null;
    };
  }, [apiSession.initialized, apiSession.token, appRouteMode, infra, collabEnabled]);

  // For testing: start on a Query tab by default (create one if needed).
  // This runs once after TabManager has populated tabs.
  useEffect(() => {
    if (appRouteMode !== 'workbook') return;
    if (!infra) return;
    if (startOnQueryTabRanRef.current) return;
    if (!tabs || tabs.length === 0) return;

    const active = tabs.find(t => t.id === activeTabId);
    if (active?.kind === 'query') {
      startOnQueryTabRanRef.current = true;
      return;
    }

    const existingQuery = tabs.find(t => t.kind === 'query');
    startOnQueryTabRanRef.current = true;
    if (existingQuery) {
      void infra.tabManager.activateTab(existingQuery.id);
    } else {
      void infra.tabManager.createQuery('Query 1');
    }
  }, [appRouteMode, infra, tabs, activeTabId]);

  // Update dimensions when state changes (only if worker is active and sab needs resize - simplified here)
  // Note: This logic was in previous App.tsx but simplified for this integration step.
  // Ideally we re-use ViewportStreamer.updateSharedState if dimensions change.

  // Determine active view
  const activeTab = tabs.find(t => t.id === activeTabId);
  const isQueryTab = activeTab?.kind === 'query';
  const isDataSourceTab = activeTab?.kind === 'datasource';
  const isDashboardTab = activeTab?.kind === 'dashboard';
  useEffect(() => {
    if (appRouteMode !== 'workbook') return
    // Clear badge when user views a sheet tab.
    const id = String(activeTabId || '').trim()
    if (!id) return
    if (!/^sheet-\d+$/.test(id)) return
    setTabUpdateCounts(prev => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [activeTabId, appRouteMode])

  // Ensure the shared spreadsheet controller is restored when leaving query view.
  useEffect(() => {
    if (appRouteMode !== 'workbook') return;
    // Only run this in spreadsheet mode. Other tab kinds (query/datasource/dashboard)
    // must not reset shared layout state as it can interfere with their rendering.
    if (isQueryTab || isDataSourceTab || isDashboardTab) return;
    // Spreadsheet mode: allow layout writes to SharedState and remove frozen header rows.
    controller.setSuppressSharedLayoutWrites(false);
    controller.setFrozenRowsTop(0);
    // Restore spreadsheet dimensions (so switching back from query results doesn't leave the grid huge).
    controller.setDimensions(colCount, rowCount);
  }, [appRouteMode, isQueryTab, isDataSourceTab, isDashboardTab, controller, colCount, rowCount]);

  const bridgeMode = typeof window !== 'undefined' ? devLoginBridgeMode(window.location) : null
  if (bridgeMode) {
    return <DevLoginBridgePage mode={bridgeMode} />
  }

  const oauthRequestId = typeof window !== 'undefined' ? oauthAuthorizationRequest(window.location) : null
  if (oauthRequestId) {
    return <OAuthConsentPage requestId={oauthRequestId} />
  }

  if (typeof window !== 'undefined' && isManagedAccountLinkRoute(window.location)) {
    return <ManagedOAuthAccountLinkPage />
  }

  const publicShareToken = typeof window !== 'undefined' ? publicCfoShareToken(window.location) : null
  if (publicShareToken) {
    return <CfoPublicReportPage token={publicShareToken} />
  }

  // CFO Core marketing + self-serve onboarding surfaces.
  if (appRouteMode === 'job_inputs') {
    return <CfoJobInputsPage />
  }
  if (appRouteMode === 'cfo_landing') {
    const hasSession =
      typeof window !== 'undefined' && !!window.localStorage.getItem('fusegrid_api_token')
    const demoRedirectPath = demoHomeRedirectPath(hasSession)
    // Logged-out visitors see the marketing landing.
    if (!hasSession) {
      return <CfoLandingPage />
    }
    // The selector is an account-management surface. Demo sessions belong to
    // the Rivertown sample firm and must stay inside its demo experience.
    if (demoRedirectPath) {
      return <CfoDemoHomeRedirect path={demoRedirectPath} />
    }
    // Logged-in visitors land on the CFO experiences selector. The internal/
    // Tableau workspace stays reachable via ?view=tableau-creator, which maps to
    // appRouteMode 'admin_shell' (never enters this block) and falls through to
    // the TableauAdminShell return below.
    return (
      <>
        <CfoExperiencesPage />
        <CfoDemoBanner />
        <TourHost />
      </>
    )
  }
  if (appRouteMode === 'tour_share_entry') {
    return <TourShareEntryPage />
  }
  if (appRouteMode === 'cfo_pricing') {
    return <CfoPricingPage />
  }
  if (appRouteMode === 'cfo_signup') {
    return <CfoSignupPage />
  }
  if (appRouteMode === 'cfo_demo_entry') {
    return <CfoDemoEntryPage />
  }
  if (appRouteMode === 'cfo_onboarding') {
    return <CfoSyncStartedPage />
  }
  if (appRouteMode === 'cfo_connect_checklist') {
    return <CfoConnectChecklistPage />
  }
  if (appRouteMode === 'billing_checkout_complete') {
    return <CfoCheckoutCompletePage />
  }
  if (appRouteMode === 'partner_billing') {
    return <CfoPartnerBillingPage />
  }
  if (appRouteMode === 'cfo_legal_privacy') {
    return <CfoLegalPage doc="privacy" />
  }
  if (appRouteMode === 'cfo_legal_terms') {
    return <CfoLegalPage doc="terms" />
  }

  if (appRouteMode === 'founder_activity') {
    return (
      <>
        {renderTopChrome()}
        <FounderConsole topInsetPx={topInsetPx} />
      </>
    )
  }

  if (appRouteMode === 'firm_cockpit') {
    return (
      <>
        {renderTopChrome()}
        <CfoFirmCockpitApp topInsetPx={topInsetPx} />
        <CfoDemoBanner />
        <TourHost />
      </>
    )
  }

  if (appRouteMode === 'workbook') {
    return (
      <>
        {renderTopChrome()}
        <Suspense
          fallback={(
            <LoadingOverlay
              label="Loading workbook…"
              style={{ position: 'fixed', top: topInsetPx }}
            />
          )}
        >
          <TableauCreatorPage client={infra?.client || null} topInsetPx={topInsetPx} />
        </Suspense>
      </>
    )
  }

  if (appRouteMode === 'cfo_core') {
    return (
      <>
        {renderTopChrome()}
        <CfoCoreApp workerClient={shouldStartAppWorker ? infra?.client || null : null} topInsetPx={topInsetPx} />
        <CfoDemoBanner />
        <TourHost />
      </>
    )
  }

  if (appRouteMode === 'client_workspace') {
    return (
      <>
        {renderTopChrome()}
        <ClientOperatingWorkspacePage topInsetPx={topInsetPx} />
        <CfoDemoBanner />
        <TourHost />
      </>
    )
  }

  if (appRouteMode === 'excel_mockups') {
    return (
      <>
        {renderTopChrome()}
        <ExcelChromeMockups />
      </>
    )
  }

  if (appRouteMode === 'excel_live_shell') {
    return (
      <>
        {renderTopChrome()}
        <ExcelLiveChromeFixture />
      </>
    )
  }

  if (appRouteMode === 'surface_admin_fixture') {
    return (
      <>
        {renderTopChrome()}
        <FuseGridSurfaceAdminFixture />
      </>
    )
  }

  if (appRouteMode === 'surface_datasource_fixture') {
    return (
      <>
        {renderTopChrome()}
        <FuseGridSurfaceDataSourceFixture />
      </>
    )
  }

  if (appRouteMode === 'surface_viz_authoring_fixture') {
    return (
      <>
        {renderTopChrome()}
        <FuseGridSurfaceVizAuthoringFixture />
      </>
    )
  }

  if (appRouteMode === 'surface_viz_edit_fixture') {
    return (
      <>
        {renderTopChrome()}
        <FuseGridSurfaceVizEditFixture />
      </>
    )
  }

  return (
    <>
      {renderTopChrome()}
      <TableauAdminShell topInsetPx={topInsetPx} />
    </>
  );
}

export default App;
